import React from 'react';
import { Linkedin, ArrowUpRight, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <footer className="border-t border-border bg-muted/30">
      <div className="container px-4 md:px-6 py-16 max-w-screen-xl mx-auto">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="space-y-4 lg:col-span-1">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold tracking-tight text-foreground">
                NoQ5<span className="text-primary">.ai</span>
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The AI-Powered Revenue Intelligence Platform that runs your CRM so your team closes deals. Built in Ireland, GDPR compliant.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <a 
                href="https://www.linkedin.com/company/noq5/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/30 transition-all"
                aria-label="Follow NoQ5.ai on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a 
                href="mailto:hello@noq5.ai"
                className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/30 transition-all"
                aria-label="Email NoQ5.ai"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>
          
          {/* Product */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">Product</h3>
            <div className="space-y-3 text-sm">
              <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group">
                About Us
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <Link to="/pricing" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group">
                Pricing
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <Link to="/comparison" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group">
                Compare
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group">
                Blog
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            </div>
          </div>
          
          {/* Resources */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">Resources</h3>
            <div className="space-y-3 text-sm">
              <Link to="/docs" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group">
                Documentation
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <a 
                href="https://app.noq5.ai/docs/hubspot-setup"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group"
              >
                HubSpot Setup
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <Link to="/faq" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group">
                FAQ
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            </div>
          </div>
          
          {/* Legal */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">{t('footer.legal')}</h3>
            <div className="space-y-3 text-sm">
              <a 
                href="https://app.noq5.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group"
              >
                {t('footer.privacyPolicy')}
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <a 
                href="https://app.noq5.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group"
              >
                {t('footer.termsOfService')}
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
            
            {/* Trust badges */}
            <div className="pt-4 space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="text-base">🇪🇺</span>
                <span>EU-Based & GDPR Compliant</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="text-base">🔒</span>
                <span>ISO 27001 in progress</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} NoQ5.ai Technologies Ltd. All rights reserved.
          </div>
          <div className="text-sm text-muted-foreground">
            Made with ❤️ in Dublin, Ireland
          </div>
        </div>
      </div>
    </footer>
  );
};
